/** @jsx jsx */

/**  @jsxFrag jsx  */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { memo } from 'react';

import moment from 'moment';
import { compose, withProps } from 'recompose';

import useRouter from 'js/lib/useRouter';
import { formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { CardClips, CardEnrollmentDate, CardWatchNow, Grid, breakpoints } from '@coursera/cds-core';
import type { GridProps, ProductCardProps } from '@coursera/cds-core';

import withEnterpriseProgramMetadata from 'bundles/enterprise-learner-search/components/search/withEnterpriseProductMetadata';
import type {
  PrivateSession,
  Props as PropsFromWithEnterpriseProductMetadata,
} from 'bundles/enterprise-learner-search/components/search/withEnterpriseProductMetadata';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import getDurationFormatted from 'bundles/enterprise-legacy-learner-home/utils/getDurationFormatted';
import {
  launchMiniModal,
  shouldRedirectToUnifiedDescriptionPage,
} from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import {
  getImagePropOverrides,
  getPartnerNamesAndLogos,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import { getProductCardStatusTags } from 'bundles/product-card/utils/getProductCardStatusTags';
import { ProductsCardsCdsPlaceholder } from 'bundles/search-common/components/search-cards/Placeholder';
import { LEARNING_PRODUCTS_ENUM_MAP, getTranslatedDifficulty } from 'bundles/search-common/constants';
import type { LEARNING_PRODUCTS, PRODUCT_DIFFICULTY_LEVELS } from 'bundles/search-common/constants';
import type { SearchProductHit } from 'bundles/search-common/providers/searchTypes';
import type { SearchIndexHit } from 'bundles/search-common/types';
import { getIsPathwayContent } from 'bundles/search-common/utils/SearchUtils';
import { getCleanIdFromObjectId } from 'bundles/search-common/utils/getCleanIdFromObjectId';
import numberRenders from 'bundles/teach-course-shared/lib/numberRenders';

import _t from 'i18n!nls/program-home';

const styles = {
  container: css`
    list-style: none;
    padding-left: 0;
  `,
  searchCardWrapper: css`
    height: 100%;
    padding: 0 6px 24px;
  `,
  /* TrackedLink2 adds an extra div that interferes with the search card styling. 
  This wrapper div applies style to that extra div to ensure the search cards are styled correctly. */
  targetSearchCardWrapper: css`
    height: 100%;
    padding: 0 6px 24px;

    > * {
      height: inherit;
    }
    ${breakpoints.down('xs')} {
      padding: 0 0 24px 0;
    }
  `,
};

export type EnterpriseCardData = {
  objectID: string;
  hitPosition: number;
  query?: string;
  queryID: string;
  listIndex: number;
  hasNoResults: boolean;
  filtersApplied: Record<string, string[]>;
  indexName?: string;
  indexPosition?: number;
};

type ProductForEnterpriseMetadata = {
  id: string;
  offeringType: string; // using a string because of built in translator in the HOC
};

type PropsForEnterpriseMetadata = {
  products: ProductForEnterpriseMetadata[];
};

type InputProps = {
  hits?: SearchProductHit[];
  isLoading?: boolean;
  noResults?: boolean;
  resultsPerPage: number;
  eventingData?: {
    searchIndex: string;
    searchIndexPosition: number;
    page?: string;
    searchQuery?: string;
    filtersApplied?: Record<string, string[]>;
  };
  searchCardImageProps?: { fit: string };
  programId?: string;
  thirdPartyOrganizationId?: string;
  renderSearchHit?: (hit: SearchProductHit, cardData?: EnterpriseCardData) => React.ReactNode;
  placeholder?: JSX.Element;
  gridSpacing?: GridProps['spacing'];
  variant?: ProductCardProps['variant'];
  renderAdminContent?: (hit: SearchProductHit) => ProductCardProps['adminContent'];
  onSearchCardClick?: (hit: SearchProductHit) => void;
};

type PropsToComponent = InputProps & PropsFromWithEnterpriseProductMetadata;

const LAZY_LOAD_THRESHOLD = 5;

const getVideoMetadataFromHit = (hit: SearchIndexHit): string[] => {
  let section1 = hit.entityType === 'VIDEO' ? _t('Video') : _t('Lesson');
  if (hit.videosInLesson) {
    section1 += _t(' (#{videosInLesson} videos)', { videosInLesson: hit.videosInLesson });
  }
  const section2 = getDurationFormatted(hit.durationMs ?? 0);
  return [section1, section2];
};

const getEnterprisePrivateSession = (id: string, privateSessions?: Array<PrivateSession | null>) => {
  return privateSessions?.find((session) => session?.productId === id);
};

export const isShortFormContent = (entityType?: string | null) => ['VIDEO', 'LESSON'].includes(entityType || '');

export const combineHitData = (hits: SearchProductHit[], privateSessions?: Array<PrivateSession | null>) =>
  hits.map((hit) => {
    const searchableId = getCleanIdFromObjectId(hit.id || '') || '';
    const hitPrivateSession = getEnterprisePrivateSession(searchableId, privateSessions);

    if (hitPrivateSession?.startsAt && hitPrivateSession?.endsAt) {
      const startsAt = formatDateTimeDisplay(hitPrivateSession?.startsAt, 'MMM D');
      const endsAt = formatDateTimeDisplay(hitPrivateSession?.endsAt, 'MMM D, YYYY');
      const session = `${startsAt} - ${endsAt}`;

      return {
        ...hit,
        session,
      };
    } else {
      return hit;
    }
  });

type productType = (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS];
const mapToSearchIndexHit = (hits?: SearchProductHit[]): SearchIndexHit[] => {
  // TODO: refactor EnterpriseSearchCard to take SearchProductHit and remove this mapping
  return (
    hits?.map((hit) => {
      const productDifficultyLevel = getTranslatedDifficulty(
        hit.productDifficultyLevel || ''
      ) as (typeof PRODUCT_DIFFICULTY_LEVELS)[keyof typeof PRODUCT_DIFFICULTY_LEVELS];
      return {
        ...hit,
        id: hit.id || undefined,
        name: hit.translatedName || hit.name || undefined,
        imageUrl: hit.imageUrl || undefined,
        isFree: !!hit.isCourseFree,
        isPartOfCourseraPlus: !!hit.isPartOfCourseraPlus,
        isCreditEligible: !!hit.isCreditEligible,
        isNewContent: hit.isNewContent || undefined,
        isPathwayContent: getIsPathwayContent(hit) || undefined,
        entityType: hit.productType as productType,
        objectUrl: hit.url || undefined,
        productDifficultyLevel,
        productDuration: hit.productDuration || undefined,
        partners: hit.partners || undefined,
        partnerLogos: hit.partnerLogos || undefined,
        avgProductRating: hit.avgProductRating || undefined,
        numProductRatings: hit.numProductRatings || undefined,
        skills: hit.translatedSkills || hit.skills || undefined,
        parentCourseName: hit.translatedParentCourseName || hit.parentCourseName || undefined,
        parentLessonName: hit.translatedParentLessonName || hit.parentLessonName || undefined,
        videosInLesson: hit.videosInLesson || undefined,
        completions: hit.completions || undefined,
        cobrandingEnabled: !!hit.cobrandingEnabled,
      };
    }) || []
  );
};

export const EnterpriseSearchCards = ({
  hits,
  noResults = false,
  resultsPerPage,
  eventingData,
  privateSessions,
  renderSearchHit,
  placeholder,
  gridSpacing,
  isLoading,
  variant,
  renderAdminContent,
  onSearchCardClick,
}: PropsToComponent) => {
  const router = useRouter();

  if (isLoading || !hits) {
    if (placeholder) return placeholder;
    return <ProductsCardsCdsPlaceholder {...{ resultsPerPage, variant }} />;
  }

  const combinedHits = combineHitData(hits, privateSessions);
  const trackingName = noResults ? 'no_results_search_card' : 'search_card';
  // Directly link to unified description page if no in multi-program home.
  const redirectToUnifiedDescriptionPage = shouldRedirectToUnifiedDescriptionPage(router);

  return (
    <Grid
      container
      component="ul"
      css={styles.container}
      spacing={gridSpacing}
      className="rc-EnterpriseSearchCards"
      role="region"
      aria-label={_t('Search Results')}
      id="searchResults"
    >
      {combinedHits.length < 1 && <div>{_t('No results found')}</div>}
      {combinedHits.map((hit: SearchProductHit, hitPosition) => {
        const isSFC = isShortFormContent(hit.productType);
        const searchQuery = router.location.search;
        let urlToUse = redirectToUnifiedDescriptionPage
          ? `/programs/${router.params.programSlug}${hit.url}?source=search`
          : undefined;
        urlToUse = isSFC ? `${hit.url}${searchQuery}` : urlToUse;
        const entityType = LEARNING_PRODUCTS_ENUM_MAP[hit.productType?.toUpperCase() || ''];
        const mappedHit: SearchIndexHit = {
          ...mapToSearchIndexHit([hit])[0],
          productDuration: undefined, // removed for feature parity
          entityType,
          durationMs: moment.duration(hit.duration).asMilliseconds(),
        };

        const cardData: EnterpriseCardData = {
          objectID: hit.id ?? '',
          hitPosition,
          query: eventingData?.searchQuery || '',
          queryID: eventingData?.searchQuery || '',
          listIndex: Number(eventingData?.page) || 1,
          hasNoResults: noResults,
          filtersApplied: eventingData?.filtersApplied || {},
          indexName: eventingData?.searchIndex,
          indexPosition: eventingData?.searchIndexPosition,
        };

        if (renderSearchHit) {
          return renderSearchHit(hit, cardData);
        }

        const partners = mappedHit.partners
          ? getPartnerNamesAndLogos(
              mappedHit.partners.map((partner) => {
                return { name: partner };
              }),
              mappedHit.cobrandingEnabled,
              mappedHit.partnerLogos
            )
          : undefined;

        const adminContent = renderAdminContent?.(hit);
        const slug = mappedHit.objectUrl?.substring(mappedHit.objectUrl?.lastIndexOf('/') + 1);

        return (
          <Grid
            key={hit.id}
            item
            md={variant === 'list' ? 12 : 4}
            sm={variant === 'list' ? 12 : 6}
            xs={12}
            component="li"
          >
            <div css={styles.targetSearchCardWrapper}>
              <LearningProductCard
                bodyContent={
                  isSFC && mappedHit.parentCourseName ? (
                    <CardClips
                      clipInfo={{ courseName: mappedHit.parentCourseName, lessonName: mappedHit?.parentLessonName }}
                    />
                  ) : null
                }
                ImageComponent={
                  <LearningProductCardImage
                    imageUrl={mappedHit?.imageUrl || ''}
                    enableFluidWidthForLazyImg={true}
                    enableLazyLoading={hitPosition > LAZY_LOAD_THRESHOLD}
                    imageParams={{
                      ...getImagePropOverrides(partners ?? [], mappedHit.imageUrl ?? ''),
                    }}
                  />
                }
                preBadgeContent={
                  <div>
                    {mappedHit?.session && !adminContent && (
                      <CardEnrollmentDate>{mappedHit?.session}</CardEnrollmentDate>
                    )}
                    {isSFC && (
                      <CardWatchNow
                        watchNow={{ viewCount: numberRenders.Formatters.largeNumber(hit.completions ?? 0) }}
                      />
                    )}
                  </div>
                }
                badgeConfig={{
                  isPathwayContent: !isSFC && mappedHit?.isPathwayContent,
                  productType: mappedHit?.entityType,
                }}
                linkProps={{
                  href: urlToUse,
                  target: urlToUse ? '_blank' : undefined,
                  rel: urlToUse ? 'noopener noreferrer' : undefined,
                  component: TrackedLink2,
                }}
                skills={!isSFC ? mappedHit.skills : undefined}
                statusTags={getProductCardStatusTags({ isNew: false, statusTags: [], slug })}
                partners={partners}
                name={mappedHit.name || ''}
                id={mappedHit.id}
                slug={slug}
                numProductRatings={mappedHit.numProductRatings}
                avgProductRating={mappedHit.avgProductRating}
                productType={mappedHit?.entityType}
                productDifficultyLevel={mappedHit.productDifficultyLevel}
                productDuration={mappedHit.productDuration}
                variant={variant}
                cardIndex={hitPosition}
                indexName={eventingData?.searchIndex}
                adminContent={adminContent}
                customMetadata={isSFC ? getVideoMetadataFromHit(mappedHit) : undefined}
                trackingProps={{
                  trackedLinkProps: {
                    trackingName,
                    withVisibilityTracking: true,
                    requireFullyVisible: true,
                    data: cardData,
                  },
                  additionalV3TrackingData: {
                    searchedQuery: eventingData?.searchQuery || '',
                  },
                }}
                customOnClick={(() => {
                  // Admin context -> Always use external click handler when provided (highest priority)
                  if (onSearchCardClick) {
                    return () => onSearchCardClick(hit);
                  }

                  // Has PDP URL -> Link directly to PDP
                  if (urlToUse) {
                    return undefined;
                  }

                  // Learner context with no URL (Multi-program) -> Open mini modal
                  const productIdForMiniModal = getCleanIdFromObjectId(hit.id || '');
                  if (!productIdForMiniModal) return undefined; // Skip mini modal if we can't get a valid product ID
                  const isCourseType = (hit.productType || '').toLowerCase() === 'course';
                  return () => launchMiniModal(router, productIdForMiniModal, isCourseType, undefined);
                })()}
              />
            </div>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default memo(
  compose<PropsToComponent, InputProps>(
    withProps<PropsForEnterpriseMetadata, PropsToComponent>(({ hits }) => {
      const filteredHits = filterExistsOrDefault(hits);

      const products = filteredHits
        .filter((hit) => !!hit.id && !!hit.productType)
        .map(({ id, productType }) => ({
          id: getCleanIdFromObjectId(id || '') || '',
          offeringType: String(productType),
        }));

      return {
        products,
      };
    }),
    withEnterpriseProgramMetadata
  )(EnterpriseSearchCards)
);
